import { useEffect, useState } from 'react';
export function useRangeInput(min, max, minValue, maxValue, format, onChange) {
    const DEFAULT_MIN = 0;
    const DEFAULT_MAX = 100000;
    const DECIMAL_PLACES = 2;
    const STEP_DECIMAL = Number((1 / Math.pow(10, DECIMAL_PLACES)).toFixed(DECIMAL_PLACES));
    const rangeMin = typeof min === 'number' ? min : DEFAULT_MIN;
    const rangeMax = typeof max === 'number' ? max : DEFAULT_MAX;
    const currentMin = typeof minValue === 'number' ? minValue : rangeMin;
    const currentMax = typeof maxValue === 'number' ? maxValue : rangeMax;
    const leftPercent = ((currentMin - rangeMin) / (rangeMax - rangeMin)) * 100;
    const rightPercent = ((currentMax - rangeMin) / (rangeMax - rangeMin)) * 100;
    const [minText, setMinText] = useState(typeof minValue === 'number' ? format(currentMin) : undefined);
    const [maxText, setMaxText] = useState(typeof maxValue === 'number' ? format(currentMax) : undefined);
    const [minFocused, setMinFocused] = useState(false);
    const [maxFocused, setMaxFocused] = useState(false);
    useEffect(() => {
        if (!minFocused) {
            if (typeof minValue === 'number') {
                setMinText(format(currentMin));
            }
            else {
                setMinText(undefined);
            }
        }
    }, [currentMin, minFocused, minValue, format]);
    useEffect(() => {
        if (!maxFocused) {
            if (typeof maxValue === 'number') {
                setMaxText(format(currentMax));
            }
            else {
                setMaxText(undefined);
            }
        }
    }, [currentMax, maxFocused, maxValue, format]);
    function clamp(localValue, minLocal, maxLocal) {
        return Math.min(Math.max(localValue, minLocal), maxLocal);
    }
    function roundToDecimals(localValue) {
        return Math.round(localValue * Math.pow(10, DECIMAL_PLACES)) / Math.pow(10, DECIMAL_PLACES);
    }
    function sanitizeNumericText(text) {
        let result = '';
        let seenSeparator = false;
        let fractionLength = 0;
        for (const ch of text) {
            if (ch >= '0' && ch <= '9') {
                if (seenSeparator) {
                    if (fractionLength >= DECIMAL_PLACES)
                        continue;
                    fractionLength += 1;
                }
                result += ch;
                continue;
            }
            if ((ch === '.' || ch === ',') && !seenSeparator) {
                seenSeparator = true;
                result += ch;
            }
        }
        return result;
    }
    function onMinFocus() {
        setMinFocused(true);
    }
    function onMaxFocus() {
        setMaxFocused(true);
    }
    function onMinBlur() {
        setMinFocused(false);
    }
    function onMaxBlur() {
        setMaxFocused(false);
    }
    function onMinRangeChange(event) {
        const raw = String(event.target.value ?? '');
        if (!raw.trim()) {
            onChange({ minValue: undefined, maxValue });
            return;
        }
        const value = Number(raw);
        const rounded = roundToDecimals(value);
        const clampedMin = Math.min(clamp(rounded, rangeMin, rangeMax), currentMax);
        onChange({ minValue: clampedMin, maxValue });
    }
    function onMaxRangeChange(event) {
        const raw = String(event.target.value ?? '');
        if (!raw.trim()) {
            onChange({ minValue, maxValue: undefined });
            return;
        }
        const value = Number(raw);
        const rounded = roundToDecimals(value);
        const clampedMax = Math.max(clamp(rounded, rangeMin, rangeMax), currentMin);
        onChange({ minValue, maxValue: clampedMax });
    }
    function onMinTextChange(event) {
        const numericText = sanitizeNumericText(event.target.value);
        setMinText(numericText);
        const normalized = numericText.replace(',', '.');
        if (!normalized || normalized === '.' || normalized === ',') {
            onChange({ minValue: undefined, maxValue });
            return;
        }
        const parsedRaw = parseFloat(normalized);
        if (Number.isFinite(parsedRaw)) {
            const rounded = roundToDecimals(parsedRaw);
            const clampedMin = Math.min(clamp(rounded, rangeMin, rangeMax), currentMax);
            onChange({ minValue: clampedMin, maxValue });
        }
    }
    function onMaxTextChange(event) {
        const numericText = sanitizeNumericText(event.target.value);
        setMaxText(numericText);
        const normalized = numericText.replace(',', '.');
        if (!normalized || normalized === '.' || normalized === ',') {
            onChange({ minValue, maxValue: undefined });
            return;
        }
        const parsedRaw = parseFloat(normalized);
        if (Number.isFinite(parsedRaw)) {
            const rounded = roundToDecimals(parsedRaw);
            const clampedMax = Math.max(clamp(rounded, rangeMin, rangeMax), currentMin);
            onChange({ minValue, maxValue: clampedMax });
        }
    }
    function onKeyDownNumeric(e) {
        const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab', '.', ','];
        if (allowedKeys.includes(e.key))
            return;
        if (!/^\d$/.test(e.key)) {
            e.preventDefault();
        }
    }
    return {
        rangeMin,
        rangeMax,
        stepDecimal: STEP_DECIMAL,
        currentMin,
        currentMax,
        leftPercent,
        rightPercent,
        minText,
        maxText,
        onMinTextChange,
        onMaxTextChange,
        onKeyDownNumeric,
        onMinFocus,
        onMaxFocus,
        onMinBlur,
        onMaxBlur,
        onMinRangeChange,
        onMaxRangeChange,
    };
}
